import React from 'react';

import {
  BaseCardData,
  RulesCardData,
  ThresholdsComparison,
  TierColumn,
  TierRow,
  ModelPageTarget,
  baseWord,
  cutoffsText,
  groupText,
  pointsText,
} from './rulesCardData';
import './scoreCard.css';
import './rulesCard.css';

const TIERS = ['Very Good', 'Good', 'Medium', 'Low'];

const CHANGE_LABEL: Record<string, string> = {
  added: 'Added',
  removed: 'Removed',
  changed: 'Changed',
  renamed: 'Renamed',
};

function StateTag({ state }: { state: 'proposed' | 'saved' }) {
  return (
    <span className={`mrules-state mrules-state--${state}`}>
      {state === 'saved' ? 'Saved' : 'Proposed'}
    </span>
  );
}

/** The button to the page a card's change lives on. */
export function OpenPage({
  label,
  target,
  onOpen,
}: {
  label: string;
  target: ModelPageTarget;
  onOpen?: (target: ModelPageTarget) => void;
}) {
  if (!onOpen) return null;
  return (
    // The jump chip every other link in the chat uses, so this reads as "go there"
    // rather than as a new kind of control.
    <button
      type="button"
      className="mcp-jump mrules-open"
      data-testid={`open-${target}`}
      onClick={() => onOpen(target)}
    >
      {label}
      {/* Wrapped: the FontAwesome kit swaps the <i> for an <svg>, and the span is the
          element whose spacing survives that. */}
      <span className="mrules-open-go" aria-hidden="true">
        <i className="fas fa-arrow-right" />
      </span>
    </button>
  );
}

function rangeText(range: { min: number; max: number } | null | undefined) {
  return range ? `${range.min} to ${range.max}` : null;
}

/**
 * A rules change as a table: one row per rule it touches, before and after.
 *
 * Drawn on the preview, marked Proposed, because that is when the change is reviewed, and
 * again once written, marked Saved, with what the tiers deliver now. Borrows the score
 * card's frame rather than adding another look for one more card.
 */
export function RulesCard({
  card,
  onOpen,
}: {
  card: RulesCardData;
  onOpen?: (target: ModelPageTarget) => void;
}) {
  const before = rangeText(card.range_before);
  const after = rangeText(card.range_after);
  const reasons = card.rows.some((row) => row.why);
  return (
    <section className="mscore" data-testid="rules-card">
      <header className="mscore-hd">
        <span className="mscore-who">Rule changes</span>
        <StateTag state={card.state} />
        <OpenPage label="Open Rules" target="rules" onOpen={onOpen} />
      </header>
      <div className="mscore-scroll">
        <table className="mscore-table">
          <thead>
            <tr>
              <th>Group</th>
              <th>Rule</th>
              <th className="mscore-r">Before</th>
              <th className="mscore-r">After</th>
              {reasons && <th>Why</th>}
            </tr>
          </thead>
          <tbody>
            {card.rows.map((row, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i} className={`mrules-row mrules-row--${row.change}`}>
                <th scope="row">{groupText(row)}</th>
                <td>
                  <span className="mrules-change">
                    {CHANGE_LABEL[row.change] ?? row.change}
                  </span>{' '}
                  {row.change === 'renamed' ? '' : row.rule || 'A rule'}
                  {row.not_in_data?.length ? (
                    <div className="mrules-unseen">
                      Not in the data: {row.not_in_data.join(', ')}
                    </div>
                  ) : null}
                </td>
                <td className="mscore-r">
                  {row.change === 'renamed'
                    ? row.was || '-'
                    : pointsText(row.was)}
                </td>
                <td className="mscore-r">
                  {row.change === 'renamed'
                    ? row.now || '-'
                    : pointsText(row.now)}
                </td>
                {reasons && <td className="mrules-why">{row.why || ''}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {after && before !== after && (
        <p className="mscore-note">
          Range: {after} (was {before})
        </p>
      )}
      {card.state === 'saved' && card.says && (
        <p className="mscore-note">{card.says}</p>
      )}
    </section>
  );
}

function tierCell(column: TierColumn, tier: string) {
  const row: TierRow | undefined = column.tiers.find((t) => t.tier === tier);
  if (!row) return <span className="mrules-dim">-</span>;
  return (
    <>
      <b>{row.leads_pct ?? '-'}%</b>
      <span className="mrules-dim"> of leads</span>
      <br />
      {row.share_of_all_conversions_pct ?? '-'}%
      <span className="mrules-dim"> of conversions</span>
      {typeof row.vs_average === 'number' && (
        <>
          <br />
          {row.vs_average}x<span className="mrules-dim"> avg</span>
        </>
      )}
    </>
  );
}

function staggerText(staggers: boolean | null | undefined) {
  if (staggers === true) return 'Holds';
  if (staggers === false) return 'Breaks';
  return '-';
}

/**
 * Tier cutoffs compared: today's first, then every candidate tried in this turn.
 *
 * One table however many previews the agent ran, because the question is always which
 * column to pick, and that is answered by reading across a row.
 */
export function ThresholdsCard({
  comparison,
  onOpen,
}: {
  comparison: ThresholdsComparison;
  onOpen?: (target: ModelPageTarget) => void;
}) {
  const saved = comparison.candidates.some((c) => c.state === 'saved');
  const columns: Array<{ label: string; column: TierColumn }> = [
    { label: 'Today', column: comparison.current },
    ...comparison.candidates.map((column) => ({
      label: column.state === 'saved' ? 'Saved' : 'Proposed',
      column,
    })),
  ];
  return (
    <section className="mscore" data-testid="thresholds-card">
      <header className="mscore-hd">
        <span className="mscore-who">Tier cutoffs</span>
        <StateTag state={saved ? 'saved' : 'proposed'} />
        <OpenPage
          label="Open Fine tuning"
          target="thresholds"
          onOpen={onOpen}
        />
      </header>
      <div className="mscore-scroll">
        <table className="mscore-table mrules-compare">
          <thead>
            <tr>
              <th>Tier</th>
              {columns.map(({ label, column }, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <th key={i} className={i === 0 ? 'mrules-today' : undefined}>
                  {label}
                  <div className="mrules-cuts">
                    {cutoffsText(column.thresholds)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TIERS.map((tier) => (
              <tr key={tier}>
                <th scope="row">{tier}</th>
                {columns.map(({ column }, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <td key={i} className={i === 0 ? 'mrules-today' : undefined}>
                    {tierCell(column, tier)}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <th scope="row">Order</th>
              {columns.map(({ column }, i) => (
                <td
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  className={`mrules-order mrules-order--${String(
                    column.staggers
                  )}`}
                >
                  {staggerText(column.staggers)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** The receipt for a base switch. */
export function BaseCard({ card }: { card: BaseCardData }) {
  return (
    <section className="mscore" data-testid="base-card">
      <header className="mscore-hd">
        <span className="mscore-who">Now scores with {baseWord(card.to)}</span>
      </header>
      {card.thresholds_kept && (
        <p className="mscore-note">{card.thresholds_kept}</p>
      )}
    </section>
  );
}
